// Centralized derivation of GA4-aligned attribution labels for the UI.
// Backend stores raw values (source, medium, channel, endpoint); the UI
// derives short, friendly hybrid labels from those raw values.
//
// See STATUS/PLAN_SOURCE_MEDIUM_MODEL.md for the source-of-truth rules.
// Every consumer (table cell, dashboard card, filter option list, CSV
// export) imports from here so labels never drift across surfaces.

const PAID_FB_MEDIUMS = new Set(['cpc', 'paid_social']);
const ORGANIC_FB_MEDIUMS = new Set(['social', 'organic']);

/**
 * Hybrid Source label derived from raw source + medium.
 *
 *   google + cpc                 → "Google Ads"
 *   google + organic             → "Google (organic)"
 *   facebook + cpc|paid_social   → "Meta Ads"
 *   facebook + social|organic    → "Facebook (organic)"
 *   instagram + social|organic   → "Instagram (organic)"  (split from facebook 2026-09-07)
 *   tiktok + social|organic      → "TikTok (organic)"     (referrer rule added 2026-09-07)
 *   bing + organic               → "Bing"
 *   * + referral                 → "Referral"
 *   * + email                    → "Email"
 *   direct + none                → "Direct"
 *   * + organic                  → "Organic"   (generic catch-all)
 *   fallback                     → capitalized raw source value
 */
export function getSourceLabel(
  source: string | null | undefined,
  medium: string | null | undefined,
): string {
  const s = source ?? '';
  const m = medium ?? '';

  if (s === 'google' && m === 'cpc') return 'Google Ads';
  if (s === 'google' && m === 'organic') return 'Google (organic)';
  if (s === 'facebook' && PAID_FB_MEDIUMS.has(m)) return 'Meta Ads';
  if (s === 'facebook' && ORGANIC_FB_MEDIUMS.has(m)) return 'Facebook (organic)';
  if (s === 'instagram' && ORGANIC_FB_MEDIUMS.has(m)) return 'Instagram (organic)';
  if (s === 'tiktok' && ORGANIC_FB_MEDIUMS.has(m)) return 'TikTok (organic)';
  if (s === 'bing' && m === 'organic') return 'Bing';
  if (m === 'referral') return 'Referral';
  if (m === 'email') return 'Email';
  if (s === 'direct' && m === 'none') return 'Direct';
  // Generic organic catch-all — fires after specific platform rules above so
  // 'google + organic' still labels as 'Google (organic)'. Picks up
  // other+organic, <utm_source>+organic, etc.
  if (m === 'organic') return 'Organic';
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Unknown';
}

/**
 * Hybrid Channel label derived from raw channel + endpoint.
 *
 *   whatsapp_direct | ad + whatsapp → "Meta ad → WhatsApp"  (CTWA; legacy 'ad' folded in)
 *   untracked + whatsapp        → "WhatsApp"            (channel unknown, endpoint known)
 *   untracked + <other>         → capitalized endpoint  ("Phone", "Email", etc.)
 *   website + whatsapp          → "Website → WhatsApp"
 *   <other> + <other>           → "Channel → Endpoint"  (auto-formatted)
 *
 * The word "Untracked" never appears — when channel is unknown but endpoint
 * is known, we show the endpoint we DO know. Customer-facing labels never
 * admit "we failed to track this." See PLAN_SOURCE_MEDIUM_MODEL.md.
 */
export function getChannelLabel(
  channel: string | null | undefined,
  endpoint: string | null | undefined,
): string {
  const c = channel ?? '';
  const e = endpoint ?? '';

  // CTWA (Meta click-to-WhatsApp). 'whatsapp_direct' is today's value; 'ad' is
  // legacy data from before the 2026-05-05 attribution model — both mean the
  // same Meta-ad → WhatsApp path, so they render identically.
  if ((c === 'whatsapp_direct' || c === 'ad') && e === 'whatsapp')
    return 'Meta ad → WhatsApp';
  if (c === 'untracked') return e ? formatEndpointPart(e) : 'Unknown';
  if (c === 'website' && e === 'whatsapp') return 'Website → WhatsApp';
  if (c && e) return `${formatChannelPart(c)} → ${formatEndpointPart(e)}`;
  return c ? c.charAt(0).toUpperCase() + c.slice(1) : 'Unknown';
}

function formatChannelPart(c: string): string {
  if (c === 'website') return 'Website';
  if (c === 'lead_form') return 'Lead form';
  return c.charAt(0).toUpperCase() + c.slice(1);
}

function formatEndpointPart(e: string): string {
  if (e === 'whatsapp') return 'WhatsApp';
  if (e === 'phone') return 'Phone';
  if (e === 'email') return 'Email';
  if (e === 'sms') return 'SMS';
  if (e === 'messenger') return 'Messenger';
  return e.charAt(0).toUpperCase() + e.slice(1);
}

/**
 * Maps (source, medium) to an SVG filename in /images/platforms/.
 *
 * Paid vs organic mediums get different brand identities:
 *   google + cpc                 → 'google-ads' (Google Ads wordmark)
 *   google + organic             → 'google'     (Google G — search engine)
 *   facebook + cpc|paid_social   → 'meta'       (Meta company — ad ecosystem)
 *   facebook + social|organic    → 'facebook'   (Facebook f — social network)
 *
 * Returns null for sources without a recognized platform identity (organic,
 * direct, other, bing) — render text-only, no icon, per brand sheet.
 */
export function platformLogoSlug(
  source: string | null | undefined,
  medium: string | null | undefined,
): string | null {
  const m = medium ?? '';
  switch (source) {
    case 'google':
      return m === 'cpc' ? 'google-ads' : 'google';
    case 'facebook':
      return PAID_FB_MEDIUMS.has(m) ? 'meta' : 'facebook';
    default:
      return null;
  }
}

/** Full path to a platform's logo SVG — single source for the asset convention. */
export function platformLogo(slug: string): string {
  return `/images/platforms/${slug}.svg`;
}

// ─── Filter options (consumed by LeadsToolbar in commit #5) ───────────────
//
// Each option carries a stable id, a display label, and a predicate that
// matches a lead row's raw source/medium (or channel/endpoint) values.
// Predicate-based so "Meta Ads" matches rows where source='facebook'
// AND medium IN ('cpc','paid_social') without dragging multi-value filter
// logic through TanStack column state.

export interface SourceFilterOption {
  value: string;
  label: string;
  matches: (source: string | null | undefined, medium: string | null | undefined) => boolean;
}

export const SOURCE_FILTER_OPTIONS: SourceFilterOption[] = [
  {
    value: 'google_ads',
    label: 'Google Ads',
    matches: (s, m) => s === 'google' && m === 'cpc',
  },
  {
    value: 'facebook_ads',
    label: 'Meta Ads',
    matches: (s, m) => s === 'facebook' && PAID_FB_MEDIUMS.has(m ?? ''),
  },
  {
    value: 'facebook_organic',
    label: 'Facebook (organic)',
    matches: (s, m) => s === 'facebook' && ORGANIC_FB_MEDIUMS.has(m ?? ''),
  },
  {
    value: 'instagram_organic',
    label: 'Instagram (organic)',
    matches: (s, m) => s === 'instagram' && ORGANIC_FB_MEDIUMS.has(m ?? ''),
  },
  {
    value: 'tiktok_organic',
    label: 'TikTok (organic)',
    matches: (s, m) => s === 'tiktok' && ORGANIC_FB_MEDIUMS.has(m ?? ''),
  },
  {
    value: 'organic',
    label: 'Organic',
    matches: (_s, m) => m === 'organic',
  },
  {
    value: 'direct',
    label: 'Direct',
    matches: (s) => s === 'direct',
  },
];

// Channel filter options not exported yet. Today every lead has
// endpoint=whatsapp, so a Channel filter mostly duplicates Source's
// distinctions (Direct → untracked rows; Meta Ads → CTWA rows).
// Re-add ChannelFilterOption + CHANNEL_FILTER_OPTIONS when endpoint
// diversifies (phone / email / lead_form). Channel column rendering
// is unaffected — it stays driven by getChannelLabel above.

// ─── Date formatting (migrated from leadDisplay.ts) ───────────────────────
// Relative under 24h ("just now" / "Xm ago" / "Xh ago"), real date beyond.
// Year shown only when it's not the current year.
export function formatLeadDate(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}
